import { getVisibleRealSlideIndexes } from '@theme/lib-video-player';
import { shouldReduceMotion } from '@theme/utilities';

const DEFAULT_TRANSITION_DURATION = 550;
const REDUCED_MOTION_TRANSITION_DURATION = 300;
const INTERACTION_RESUME_DELAY = 1200;
const TILT_MAX_DEGREES = 8;

/**
 * @param {HTMLElement} cardEl
 */
function initTiltEffect(cardEl) {
  if (shouldReduceMotion()) return;

  /** @type {number | null} */
  let frameId = null;

  /**
   * @param {number} rotateX
   * @param {number} rotateY
   */
  function applyTransform(rotateX, rotateY) {
    cardEl.style.transform = `perspective(50rem) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  }

  /**
   * @param {MouseEvent} event
   */
  function handleMouseMove(event) {
    const rect = cardEl.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;

    if (frameId !== null) {
      cancelAnimationFrame(frameId);
    }

    frameId = requestAnimationFrame(() => {
      applyTransform(-y * TILT_MAX_DEGREES, x * TILT_MAX_DEGREES);
      frameId = null;
    });
  }

  function handleMouseLeave() {
    if (frameId !== null) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
    cardEl.style.transform = '';
  }

  cardEl.addEventListener('mousemove', handleMouseMove);
  cardEl.addEventListener('mouseleave', handleMouseLeave);
}

/**
 * @param {HTMLElement} sectionEl
 */
function initCollectionsHoverEffects(sectionEl) {
  if (sectionEl.dataset.hoverEffect !== 'tilt') return;

  sectionEl.querySelectorAll('.collections__card').forEach((card) => {
    if (card instanceof HTMLElement) {
      initTiltEffect(card);
    }
  });
}

/**
 * Initialize hover effects for all collections sections on the page.
 */
function initAllCollectionsHoverEffects() {
  document.querySelectorAll('[data-collections]').forEach((sectionEl) => {
    if (sectionEl instanceof HTMLElement) {
      initCollectionsHoverEffects(sectionEl);
    }
  });
}

/**
 * @param {number} number
 * @param {number} total
 * @returns {string}
 */
function formatSlideStatus(number, total) {
  const template =
    Theme.translations.collections_slide_status ??
    'Slide {{ number }} of {{ total }}';
  return template
    .replace('{{ number }}', String(number))
    .replace('{{ total }}', String(total));
}

/**
 * @param {Element} swiperEl
 * @returns {Element[]}
 */
function getRealSlides(swiperEl) {
  return Array.from(
    swiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide')
  ).filter((slide) => !slide.classList.contains('swiper-slide-duplicate'));
}

/**
 * @param {HTMLElement} sectionEl
 * @param {'columnsMobile' | 'columnsTablet' | 'columnsDesktop'} key
 * @param {number} fallback
 * @returns {number}
 */
function readColumnCount(sectionEl, key, fallback) {
  const value = Number(sectionEl.dataset[key]);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * @param {Element} swiperEl
 * @param {HTMLElement} sectionEl
 * @returns {object | null}
 */
function createCollectionsCarouselConfig(swiperEl, sectionEl) {
  if (sectionEl.dataset.autoscroll !== 'true') {
    return null;
  }

  const duration = Number(sectionEl.dataset.duration) || 4000;
  const shouldAutoplay =
    sectionEl.dataset.autoscroll === 'true' &&
    !shouldReduceMotion() &&
    !window.Shopify?.designMode;
  const reverseDirection =
    sectionEl.dataset.autoscrollDirection === 'right_to_left';
  const pauseOnHover = sectionEl.dataset.pauseOnHover !== 'false';
  const pauseOnFocus = sectionEl.dataset.pauseOnFocus !== 'false';
  const gap = Number(sectionEl.dataset.gap) || 10;
  const columnsMobile = readColumnCount(sectionEl, 'columnsMobile', 1);
  const columnsTablet = readColumnCount(sectionEl, 'columnsTablet', 2);
  const columnsDesktop = readColumnCount(sectionEl, 'columnsDesktop', 3);
  const transitionDuration = shouldReduceMotion()
    ? REDUCED_MOTION_TRANSITION_DURATION
    : DEFAULT_TRANSITION_DURATION;

  let slides = getRealSlides(swiperEl);
  const hasMultipleSlides = slides.length > 1;
  const maxVisibleCount = Math.max(
    columnsMobile,
    columnsTablet,
    columnsDesktop
  );
  const shouldLoop = hasMultipleSlides && slides.length > maxVisibleCount;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let interactionResumeTimeout = null;
  /** @type {SwiperInstance | null} */
  let swiper = null;

  const abortController = new AbortController();
  const { signal } = abortController;

  /**
   * @returns {SwiperAutoplay | null}
   */
  function getControllableAutoplay() {
    if (!swiper?.autoplay || !shouldAutoplay || slides.length <= 1) return null;
    return swiper.autoplay;
  }

  function canControlAutoplay() {
    return getControllableAutoplay() !== null;
  }

  function pauseAutoplay() {
    const autoplay = getControllableAutoplay();
    if (!autoplay) return;
    if (autoplay.running && !autoplay.paused) {
      autoplay.pause();
    }
  }

  function resumeAutoplay() {
    const autoplay = getControllableAutoplay();
    if (!autoplay) return;
    if (pauseOnHover && sectionEl.matches(':hover')) return;
    if (pauseOnFocus && sectionEl.matches(':focus-within')) return;

    if (autoplay.paused && typeof autoplay.resume === 'function') {
      autoplay.resume();
      return;
    }

    if (!autoplay.running && typeof autoplay.start === 'function') {
      autoplay.start();
    }
  }

  function clearInteractionResume() {
    if (interactionResumeTimeout) {
      clearTimeout(interactionResumeTimeout);
      interactionResumeTimeout = null;
    }
  }

  function scheduleInteractionResume() {
    if (!canControlAutoplay()) return;
    clearInteractionResume();
    interactionResumeTimeout = setTimeout(function () {
      resumeAutoplay();
    }, INTERACTION_RESUME_DELAY);
  }

  function updateSlideAria() {
    slides = getRealSlides(swiperEl);
    const activeIndex = swiper?.realIndex ?? 0;
    const allSlidesVisible = swiper?.isLocked === true;

    const visibleIndexes = allSlidesVisible
      ? new Set(
          slides.map(function (_slide, index) {
            return index;
          })
        )
      : swiper
        ? getVisibleRealSlideIndexes(swiperEl, swiper)
        : new Set([activeIndex]);

    if (slides.length <= 1) {
      slides.forEach(function (slide) {
        slide.removeAttribute('aria-label');
        slide.removeAttribute('aria-hidden');

        const card = slide.querySelector('.collections__card');
        if (card instanceof HTMLElement) {
          card.removeAttribute('tabindex');
        }
      });
      return;
    }

    slides.forEach(function (slide, index) {
      const isVisible = visibleIndexes.has(index);
      slide.setAttribute(
        'aria-label',
        formatSlideStatus(index + 1, slides.length)
      );

      if (isVisible) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }

      const card = slide.querySelector('.collections__card');
      if (card instanceof HTMLElement) {
        if (isVisible) {
          card.removeAttribute('tabindex');
        } else {
          card.setAttribute('tabindex', '-1');
        }
      }
    });
  }

  /**
   * @param {Event} event
   */
  var handleKeydown = function (event) {
    if (!swiper || slides.length <= 1) return;
    if (!(event instanceof KeyboardEvent)) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        swiper.slidePrev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        swiper.slideNext();
        break;
      case 'Home':
        event.preventDefault();
        swiper.slideToLoop(0);
        break;
      case 'End':
        event.preventDefault();
        swiper.slideToLoop(slides.length - 1);
        break;
    }
  };

  var handleMouseEnter = function () {
    clearInteractionResume();
    pauseAutoplay();
    updateSlideAria();
  };

  var handleMouseLeave = function () {
    scheduleInteractionResume();
  };

  var handleFocusIn = function () {
    clearInteractionResume();
    pauseAutoplay();
  };

  /**
   * @param {Event} event
   */
  var handleFocusOut = function (event) {
    const focusEvent = /** @type {FocusEvent} */ (event);
    if (
      focusEvent.relatedTarget &&
      focusEvent.relatedTarget instanceof Node &&
      sectionEl.contains(focusEvent.relatedTarget)
    ) {
      return;
    }

    scheduleInteractionResume();
  };

  var handleVisibilityChange = function () {
    if (document.hidden) {
      pauseAutoplay();
    } else {
      resumeAutoplay();
    }
  };

  sectionEl.addEventListener('keydown', handleKeydown, { signal });

  if (pauseOnHover) {
    sectionEl.addEventListener('mouseenter', handleMouseEnter, { signal });
    sectionEl.addEventListener('mouseleave', handleMouseLeave, { signal });
  }

  if (pauseOnFocus) {
    sectionEl.addEventListener('focusin', handleFocusIn, { signal });
    sectionEl.addEventListener('focusout', handleFocusOut, { signal });
  }

  if (shouldAutoplay && sectionEl.dataset.hoverEffect !== 'none') {
    sectionEl.addEventListener(
      'mouseover',
      function (event) {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('.collections__card')) return;
        clearInteractionResume();
        pauseAutoplay();
      },
      { signal }
    );

    sectionEl.addEventListener(
      'mouseout',
      function (event) {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('.collections__card')) return;
        const relatedTarget = event.relatedTarget;
        if (
          relatedTarget instanceof Element &&
          relatedTarget.closest('.collections__card')
        ) {
          return;
        }

        scheduleInteractionResume();
      },
      { signal }
    );
  }

  document.addEventListener('visibilitychange', handleVisibilityChange, {
    signal,
  });

  return {
    slidesPerView: columnsMobile,
    spaceBetween: gap,
    loop: shouldLoop,
    speed: transitionDuration,
    autoHeight: false,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    allowTouchMove: hasMultipleSlides,
    grabCursor: hasMultipleSlides,
    breakpoints: {
      640: {
        slidesPerView: columnsTablet,
        spaceBetween: gap,
      },
      880: {
        slidesPerView: columnsDesktop,
        spaceBetween: gap,
      },
    },
    autoplay:
      shouldAutoplay && hasMultipleSlides
        ? {
            delay: duration,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            reverseDirection: reverseDirection,
          }
        : false,
    a11y: { enabled: false },
    on: {
      /**
       * @param {SwiperInstance} sw
       */
      init: function (sw) {
        swiper = sw;
        updateSlideAria();
      },
      /**
       * @param {SwiperInstance} sw
       */
      slideChangeTransitionEnd: function (sw) {
        swiper = sw;
        updateSlideAria();
      },
      /**
       * @param {SwiperInstance} sw
       */
      resize: function (sw) {
        swiper = sw;
        updateSlideAria();
      },
      /**
       * @param {SwiperInstance} sw
       */
      breakpoint: function (sw) {
        swiper = sw;
        updateSlideAria();
      },
      destroy: function () {
        abortController.abort();
        clearInteractionResume();
        swiper = null;
      },
    },
  };
}

/**
 * @returns {boolean}
 */
function hasAutoscrollCollections() {
  return (
    document.querySelector('[data-collections][data-autoscroll="true"]') !==
    null
  );
}

function initCollectionsCarousel() {
  if (!hasAutoscrollCollections()) return;

  import('@theme/lib-swiper-manager').then(function (module) {
    module.registerSlider(
      '[data-collections]',
      createCollectionsCarouselConfig
    );
  });
}

initCollectionsCarousel();

document.addEventListener('DOMContentLoaded', initAllCollectionsHoverEffects);
document.addEventListener(
  'shopify:section:load',
  initAllCollectionsHoverEffects
);
document.addEventListener(
  'shopify:block:select',
  initAllCollectionsHoverEffects
);
